import { t } from '@transifex/native';
import type { ReactNode, JSX } from 'react';
import React, {
	useRef,
	useState,
	useCallback,
	Children,
	createContext,
	useContext,
	Fragment,
	useMemo,
} from 'react';

import { cssClasses } from 'pkg/css/utils';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';

import { LargeScreen, SmallScreen } from 'components/MediaQuery';
import CloseButton from 'components/close-button';
import MaterialSymbol from 'components/material-symbols';
import Modal from 'components/modal';

import { useAppState } from 'components/application/state';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

import Button from 'design/button';
import * as Card from 'design/card';

import * as css from './styles.css';

/**
 * @todo This should probably not be exported, any places that use it should use Step instead.
 */
export function Body({
	children,
	busy,
	table,
	flex,
	noSpacing,
	spaciousBody,
}: {
	children: ReactNode;
	busy?: boolean;
	table?: boolean;
	flex?: boolean;
	noSpacing?: boolean;
	spaciousBody?: boolean;
}): JSX.Element {
	return (
		<Card.Body
			className={cssClasses(css.body, busy && css.busy, table && css.table)}
			flex={flex}
			noSpacing={noSpacing}
			spaciousBody={spaciousBody}>
			{children}
		</Card.Body>
	);
}

type PromiseFunction = () => Promise<boolean>;

type StepContext = {
	prev: () => void;
	withPrev: (callback: PromiseFunction) => void;
	setCanGoPrev: (canGoPrev: boolean) => void;
	next: () => void;
	withNext: (callback: PromiseFunction) => void;
	setCanGoNext: (canGoNext: boolean) => void;
	close: () => void;
	goTo: (slug: string) => void;
	busy: boolean;
	setBusy: (isBusy: boolean) => void;
};

export const StepModalContext = createContext<StepContext>(null);

export const useStepModalContext = () =>
	useContext<StepContext>(StepModalContext);

interface FooterCancelButtonProps {
	testid?: string;
}

// Cancel for the footerContentEnd slot, for modals that want Cancel to the
// right of the primary action: closes through the step context so the modal
// animates out like the header X does, and follows the modal's busy state
// while a request is in flight.
export function FooterCancelButton({
	testid,
}: FooterCancelButtonProps): JSX.Element {
	const { close, busy } = useStepModalContext();

	return (
		<Button disabled={busy} onClick={close} testid={testid}>
			{t('Cancel', { _context: 'modal' })}
		</Button>
	);
}

export interface StepProps {
	children: ReactNode;
	skipBody?: boolean;
	flexBody?: boolean;
	spaciousBody?: boolean;
	tableStep?: boolean;
	busy?: boolean;
	onPrev?: () => Promise<boolean | void>;
	canGoPrev?: boolean;
	prevLabel?: string;
	hidePrev?: boolean;
	prevSlug?: string;
	prevLabelIcon?: MaterialSymbolVariant;
	prevLabelIconPosition?: 'left' | 'right' | 'top';
	onNext?: () => Promise<boolean | void>;
	canGoNext?: boolean;
	nextLabel?: string;
	hideNext?: boolean;
	nextSlug?: string;
	nextLabelIcon?: MaterialSymbolVariant;
	nextLabelIconPosition?: 'left' | 'right' | 'top';
	slug?: string;
	title?: string;
	// Lets a long title wrap onto more lines instead of truncating.
	wrapTitle?: boolean;
	description?: string;
	hideLargeScreenTitle?: boolean;
	closeOnNext?: boolean;
	closeOnPrev?: boolean;
	cautiousPrev?: boolean;
	cautiousNext?: boolean;
	canClose?: boolean;
	footerContent?: ReactNode;
	// Renders after the next button — for footer actions that should sit to
	// the right of the primary action.
	footerContentEnd?: ReactNode;
	stackSmallScreenFooter?: boolean;

	showDelete?: boolean;
	onDelete?: () => Promise<boolean | void> | void;
	deleteLabel?: string;
}

export const Step = ({
	children,
	skipBody,
	flexBody = true,
	spaciousBody,
	busy,
	tableStep = false,
}: StepProps): JSX.Element => (
	<Body
		flex={flexBody}
		noSpacing={skipBody}
		spaciousBody={spaciousBody}
		busy={busy}
		table={tableStep}>
		{children}
	</Body>
);

export const StepWithoutBody = ({ children }: StepProps): JSX.Element => (
	<Fragment>{children}</Fragment>
);

export interface StepModalProps {
	children: ReactNode;
	onOpen?: () => void;
	onClose?: () => void;
	onBeforeClose?: () => boolean;
	// onFinish is called after the entire modal is completed
	onFinish?: () => void;
	canNavigate?: boolean;
	wide?: boolean;
	thin?: boolean;
	customBackdrop?: ReactNode;
	initialIndex?: number;
	hideCloseButton?: boolean;
}

export const Base = ({
	onOpen,
	onClose,
	onBeforeClose,
	children,
	canNavigate = true,
	wide = false,
	thin = false,
	customBackdrop = null,
	onFinish = null,
	initialIndex = 0,
	hideCloseButton = false,
}: StepModalProps): JSX.Element => {
	const { keyboardOpen } = useAppState();

	const modalRef = useRef(null);
	const [busy, setBusy] = useState(false);
	const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
	const [__canGoPrev, setCanGoPrev] = useState<boolean>(true);
	const [__canGoNext, setCanGoNext] = useState<boolean>(true);

	const __prevCallback = useRef<PromiseFunction>(null);
	const __nextCallback = useRef<PromiseFunction>(null);

	useComponentDidMount(() => {
		if (onOpen) {
			onOpen();
		}
	});

	// @NOTE Filter out conditional children (null children)
	const filteredChildren = Children.toArray(children).filter((n) => n);

	const currentStep = useMemo(
		() =>
			filteredChildren.find(
				(child: ReactNode, n: number) => n === currentIndex
			) as React.ReactElement<StepProps>,
		[currentIndex, filteredChildren]
	);

	const {
		title = '',
		wrapTitle = false,
		description = '',
		hideLargeScreenTitle = false,

		canGoPrev = true,
		onPrev = null,
		hidePrev = false,
		prevSlug = '',

		canGoNext = true,
		onNext = null,
		hideNext = false,
		nextSlug = '',

		closeOnNext = false,
		closeOnPrev = false,

		cautiousPrev = false,

		canClose = true,

		footerContent = null,
		footerContentEnd = null,
		stackSmallScreenFooter = false,

		showDelete = false,
		onDelete = null,
		deleteLabel,
	} = currentStep ? currentStep.props : {};

	let {
		prevLabel = '',
		nextLabel = '',
		prevLabelIcon,
		prevLabelIconPosition,
		nextLabelIcon,
		nextLabelIconPosition,
	} = currentStep ? currentStep.props : {};

	if (currentIndex === 0 && prevLabel === '') {
		prevLabel = t('Cancel', { _context: 'modal' });
	} else if (prevLabel === '') {
		prevLabel = t('Back', { _context: 'modal' });
	}

	if (nextLabel === '') {
		nextLabel = t('Continue', { _context: 'modal' });
	}

	const withPrev = useCallback((callback: PromiseFunction) => {
		__prevCallback.current = callback;
	}, []);

	const withNext = useCallback((callback: PromiseFunction) => {
		__nextCallback.current = callback;
	}, []);

	const exit = () => modalRef.current?.requestClose();

	const close = () => modalRef.current?.close();

	const goTo = useCallback(
		(slug: string) => {
			const index = filteredChildren.findIndex(
				(child: React.ReactElement<StepProps>) => child.props?.slug === slug
			);

			if (index >= 0) {
				setCurrentIndex(index);
			}
		},
		[filteredChildren]
	);

	const prev = useCallback(async () => {
		if (__prevCallback.current) {
			setBusy(true);

			const canContinue = await __prevCallback.current();

			setBusy(false);

			if (canContinue === false) {
				return;
			}
		}

		if (onPrev) {
			setBusy(true);

			const canContinue = await onPrev();

			setBusy(false);

			if (canContinue === false) {
				return;
			}
		}

		if (closeOnPrev === true) {
			close();
			return;
		}

		if (prevSlug) {
			goTo(prevSlug);
		} else if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		} else if (currentIndex === 0) {
			close();
		}

		__prevCallback.current = () => Promise.resolve(true);
	}, [currentIndex, onPrev, setBusy, prevSlug, close, goTo, closeOnPrev]);

	const next = useCallback(async () => {
		if (__nextCallback.current) {
			setBusy(true);

			const canContinue = await __nextCallback.current();

			setBusy(false);

			if (canContinue === false) {
				return;
			}
		}

		if (onNext) {
			setBusy(true);

			const canContinue = await onNext();

			setBusy(false);

			if (canContinue === false) {
				return;
			}
		}

		const isLast: boolean = currentIndex + 1 === filteredChildren.length;

		__nextCallback.current = () => Promise.resolve(true);

		if (closeOnNext === true) {
			close();
			return;
		}

		if (nextSlug) {
			goTo(nextSlug);
		} else if (!isLast) {
			setCurrentIndex(currentIndex + 1);
		} else if (isLast) {
			if (onFinish) {
				onFinish();
			}

			close();
		}
	}, [
		onNext,
		currentIndex,
		filteredChildren.length,
		closeOnNext,
		nextSlug,
		close,
		goTo,
		onFinish,
	]);

	if (!currentStep) return null;

	let canRenderPrev = true;
	if (hidePrev) {
		canRenderPrev = false;
	}

	const backDisabled = busy || !__canGoPrev || !canGoPrev;
	const nextDisabled = busy || !__canGoNext || !canGoNext;

	const modalContext = {
		prev,
		withPrev,
		setCanGoPrev,
		next,
		withNext,
		setCanGoNext,
		goTo,
		close,
		busy,
		setBusy,
	};

	let showCloseButton = true;

	if (hideCloseButton || !canClose) {
		showCloseButton = false;
	}

	const showDivider =
		(canNavigate && canRenderPrev && currentIndex !== 0) ||
		!!title ||
		showCloseButton;

	return (
		<Modal
			hideClose
			canClose={showCloseButton}
			onBeforeClose={onBeforeClose}
			forwardedRef={modalRef}
			wide={wide}
			thin={thin}
			onClose={onClose}
			customBackdrop={customBackdrop}>
			<Card.Base className={css.wrapper} aria-busy={busy}>
				{!hideLargeScreenTitle && (
					<LargeScreen>
						<Card.Header className={css.header}>
							<Card.Heading
								className={cssClasses(css.heading, wrapTitle && css.wrapTitle)}
								data-testid="step_modal.Card.Heading">
								{title}
							</Card.Heading>
							{showCloseButton && (
								<CloseButton className={css.closeButton} onClick={exit} />
							)}
						</Card.Header>
						{description && <p className={css.description}>{description}</p>}
						<Card.Divider />
					</LargeScreen>
				)}

				<SmallScreen>
					<Card.Header className={css.header}>
						{canNavigate && canRenderPrev && currentIndex !== 0 && (
							<span
								onClick={prev}
								aria-busy={busy}
								className={cssClasses(
									css.prevButton,
									backDisabled && css.disabled,
									cautiousPrev && css.cautious
								)}
								data-testid="step_modal.prev">
								<MaterialSymbol variant="chevron_left" scale={2} />
							</span>
						)}
						<Card.Heading
							className={cssClasses(css.heading, wrapTitle && css.wrapTitle)}>
							{title}
						</Card.Heading>
						{showCloseButton && (
							<CloseButton className={css.closeButton} onClick={exit} />
						)}
					</Card.Header>
					{description && <p className={css.description}>{description}</p>}
					{showDivider && <Card.Divider />}
				</SmallScreen>

				<StepModalContext.Provider value={modalContext}>
					{currentStep}
				</StepModalContext.Provider>

				{/* The footer shares the step context, so footer slot content
				    (footerContent / footerContentEnd) can reach close and busy
				    through useStepModalContext. */}
				{canNavigate && (canRenderPrev || !hideNext) && (
					<StepModalContext.Provider value={modalContext}>
						<LargeScreen>
							<Card.Divider />
							<div
								className={cssClasses(
									css.footer,
									showDelete && css.footerWithDelete
								)}>
								{showDelete && (
									<Button
										variant="caution"
										onClick={onDelete}
										busy={busy}
										testid="step_modal.delete">
										{deleteLabel ?? t('Delete')}
									</Button>
								)}
								<div className={css.buttonGroup}>
									{footerContent}
									{canRenderPrev && (
										<Button
											onClick={prev}
											busy={busy}
											icon={prevLabelIcon}
											iconPosition={prevLabelIconPosition}
											disabled={backDisabled}
											testid="step_modal.prev">
											{prevLabel}
										</Button>
									)}
									{!hideNext && (
										<Button
											variant="primary"
											onClick={next}
											busy={busy}
											icon={nextLabelIcon}
											iconPosition={nextLabelIconPosition}
											disabled={nextDisabled}
											testid="step_modal.next">
											{nextLabel}
										</Button>
									)}
									{footerContentEnd}
								</div>
							</div>
						</LargeScreen>
						{!hideNext && !keyboardOpen && (
							<SmallScreen>
								<Card.Divider />
								<div className={css.footer}>
									{showDelete && (
										<Button
											variant="caution"
											block
											onClick={onDelete}
											busy={busy}
											testid="step_modal.delete">
											{deleteLabel ?? t('Delete')}
										</Button>
									)}
									<div
										className={cssClasses(
											css.buttonGroup,
											stackSmallScreenFooter && css.stackedButtonGroup
										)}>
										{footerContent}
										<Button
											block
											variant="primary"
											onClick={next}
											busy={busy}
											disabled={nextDisabled}
											testid="step_modal.next">
											{nextLabel}
										</Button>
										{footerContentEnd}
									</div>
								</div>
							</SmallScreen>
						)}
					</StepModalContext.Provider>
				)}
			</Card.Base>
		</Modal>
	);
};
